import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, ChevronDown, Instagram, MessageCircle, Check } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../contexts/LanguageContext";
import { tr } from "../i18n/translations";
import { COURSE_PAGES, COURSE_PAGE_LIST, tCourse } from "../i18n/course-pages";
import Header from "../components/site/Header";
import Footer from "../components/site/Footer";
import BookingDialog from "../components/site/BookingDialog";

export default function CoursePage() {
  const { slug } = useParams();
  const { lang } = useLang();
  const navigate = useNavigate();
  const page = COURSE_PAGES[slug];
  const [course, setCourse] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [openFaq, setOpenFaq] = useState(null);
  const [bookingOpen, setBookingOpen] = useState(false);

  const otherCourses = useMemo(() => COURSE_PAGE_LIST.filter((p) => p.slug !== slug), [slug]);

  useEffect(() => {
    if (!page) {
      navigate("/", { replace: true });
      return;
    }
    api.get("/courses").then((r) => {
      const found = (r.data || []).find((c) => c.slug === page.backend_slug);
      setCourse(found || null);
    }).catch(() => {});
    api.get(`/gallery?category=${page.gallery_category}`).then((r) => setGallery(r.data || [])).catch(() => {});
  }, [slug, page, navigate]);

  if (!page) return null;

  const title = tr(page.title, lang);
  const intro = tr(page.intro, lang);
  const seoTitle = tr(page.seo_title, lang);
  const seoDesc = tr(page.seo_desc, lang);
  const audience = page.audience[lang] || page.audience.es;
  const curriculum = page.curriculum[lang] || page.curriculum.es;
  const includes = page.includes[lang] || page.includes.es;
  const faq = page.faq[lang] || page.faq.es;

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      const fmt = new Intl.DateTimeFormat(
        lang === "es" ? "es-MX" : lang === "jp" ? "ja-JP" : "en-US",
        { day: "2-digit", month: "long", year: "numeric" }
      );
      return fmt.format(d);
    } catch {
      return iso;
    }
  };

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDesc} />
        <meta name="keywords" content={`curso maquillaje Aguascalientes, ${tr(page.eyebrow, "es")}, ${tr(page.eyebrow, "en")}, Abril Suarez`} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDesc} />
        <meta property="og:image" content={page.hero_image} />
        <link rel="canonical" href={`${typeof window !== "undefined" ? window.location.origin : ""}/cursos/${slug}`} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Course",
          name: tr(page.eyebrow, lang),
          description: seoDesc,
          provider: {
            "@type": "Organization",
            name: "Abril Suarez Makeup Artist",
            sameAs: "https://www.instagram.com/abrilsuamkp/",
          },
          image: page.hero_image,
          inLanguage: ["es", "en", "ja"],
          ...(course && {
            hasCourseInstance: {
              "@type": "CourseInstance",
              courseMode: "in-person",
              location: { "@type": "Place", name: "Aguascalientes, México" },
              startDate: course.next_date || undefined,
              offers: {
                "@type": "Offer",
                priceCurrency: "MXN",
                price: course.price,
                availability: "https://schema.org/InStock",
                category: "education",
              },
            },
          }),
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faq.map((item) => ({
            "@type": "Question",
            name: item.q,
            acceptedAnswer: { "@type": "Answer", text: item.a },
          })),
        })}</script>
      </Helmet>

      <Header onBook={() => setBookingOpen(true)} />

      <main className="bg-[#FAFAFA]">
        {/* HERO */}
        <section className="pt-28 pb-16 sm:pt-32 sm:pb-20" data-testid="course-hero">
          <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
            <Link to="/#courses" className="overline text-[#8B6D5E] link-underline" data-testid="course-back">
              {tr(tCourse.back, lang)}
            </Link>

            <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 mt-10 items-end">
              <div className="lg:col-span-7">
                <p className="overline text-[#8B6D5E] mb-4">{tr(page.eyebrow, lang)}</p>
                <h1 className="font-serif text-[12vw] sm:text-[8vw] lg:text-[5.5vw] leading-[1] tracking-tighter text-[#1A1A1A]" data-testid="course-title">
                  {title}
                </h1>
                <p className="mt-8 text-base sm:text-lg leading-relaxed text-[#5C5C5C] max-w-2xl">{intro}</p>

                {course && (
                  <dl className="grid grid-cols-2 sm:grid-cols-4 gap-6 mt-10 border-t border-[#E8E1DA] pt-8 max-w-2xl">
                    <div data-testid="course-meta-duration">
                      <dt className="overline text-[#8B6D5E] mb-1">{tr(tCourse.details.duration, lang)}</dt>
                      <dd className="font-serif text-xl">{course.duration}</dd>
                    </div>
                    <div data-testid="course-meta-date">
                      <dt className="overline text-[#8B6D5E] mb-1">{tr(tCourse.details.next_date, lang)}</dt>
                      <dd className="font-serif text-xl">{fmtDate(course.next_date)}</dd>
                    </div>
                    <div data-testid="course-meta-spots">
                      <dt className="overline text-[#8B6D5E] mb-1">{tr(tCourse.details.spots, lang)}</dt>
                      <dd className="font-serif text-xl">{course.spots}</dd>
                    </div>
                    <div data-testid="course-meta-price">
                      <dt className="overline text-[#8B6D5E] mb-1">{tr(tCourse.details.price, lang)}</dt>
                      <dd className="font-serif text-xl">${course.price.toLocaleString("es-MX")}</dd>
                    </div>
                  </dl>
                )}

                <div className="mt-10 flex flex-col sm:flex-row gap-4 sm:items-center">
                  <button className="btn-primary" onClick={() => setBookingOpen(true)} data-testid="course-enroll-btn">
                    {tr(tCourse.enroll_cta, lang)} <ArrowRight size={14} />
                  </button>
                  <a
                    href="https://wa.me/message/SG2ELRTG32LVF1"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="overline text-[#1A1A1A] flex items-center gap-2 link-underline"
                    data-testid="course-whatsapp"
                  >
                    <MessageCircle size={14} /> WhatsApp
                  </a>
                </div>
              </div>
              <div className="lg:col-span-5">
                <div className="zoom-frame aspect-[3/4]">
                  <img src={page.hero_image} alt={tr(page.eyebrow, lang)} loading="eager" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* AUDIENCE */}
        <section className="bg-[#F4F0EC] py-24 sm:py-32" data-testid="course-audience">
          <div className="max-w-[1400px] mx-auto px-6 sm:px-10 grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <p className="overline text-[#8B6D5E] mb-4">01</p>
              <h2 className="font-serif text-5xl sm:text-6xl tracking-tighter leading-[1]">
                {tr(tCourse.audience_title, lang)}
              </h2>
            </div>
            <ul className="lg:col-span-8 space-y-4">
              {audience.map((line, i) => (
                <li key={i} className="flex items-start gap-4 border-b border-[#1A1A1A]/15 pb-4" data-testid={`audience-${i}`}>
                  <Check size={20} className="shrink-0 mt-1 text-[#8B6D5E]" />
                  <span className="font-serif text-xl sm:text-2xl tracking-tight leading-snug">{line}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* CURRICULUM */}
        <section className="bg-[#FAFAFA] py-24 sm:py-32" data-testid="course-curriculum">
          <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
            <p className="overline text-[#8B6D5E] mb-4">02</p>
            <h2 className="font-serif text-5xl sm:text-6xl tracking-tighter leading-[1] mb-16 max-w-2xl">
              {tr(tCourse.curriculum_title, lang)}
            </h2>
            <div className="grid md:grid-cols-2 gap-x-12 gap-y-12">
              {curriculum.map((module, i) => (
                <div key={i} className="flex gap-6 border-t border-[#1A1A1A] pt-6" data-testid={`curriculum-${i}`}>
                  <p className="font-serif italic text-5xl text-[#8B6D5E] leading-none shrink-0 w-16">
                    {String(i + 1).padStart(2, "0")}
                  </p>
                  <div>
                    <h3 className="font-serif text-2xl tracking-tight mb-3">{module.t}</h3>
                    <p className="text-sm leading-relaxed text-[#5C5C5C]">{module.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* INCLUDES */}
        <section className="bg-[#1A1A1A] text-[#FAFAFA] py-24 sm:py-32" data-testid="course-includes">
          <div className="max-w-[1400px] mx-auto px-6 sm:px-10 grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <p className="overline text-[#8B6D5E] mb-4">03</p>
              <h2 className="font-serif text-5xl sm:text-6xl tracking-tighter leading-[1]">
                {tr(tCourse.includes_title, lang)}
              </h2>
            </div>
            <ul className="lg:col-span-8 divide-y divide-[#2a2a2a] border-t border-b border-[#2a2a2a]">
              {includes.map((line, i) => (
                <li key={i} className="py-6 flex items-start gap-6" data-testid={`course-include-${i}`}>
                  <span className="overline text-[#8B6D5E] pt-1">0{i + 1}</span>
                  <span className="font-serif text-xl sm:text-2xl tracking-tight">{line}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* GALLERY */}
        {gallery.length > 0 && (
          <section className="bg-[#F4F0EC] py-24 sm:py-32" data-testid="course-gallery">
            <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
              <p className="overline text-[#8B6D5E] mb-4">04</p>
              <h2 className="font-serif text-5xl sm:text-6xl tracking-tighter leading-[1] mb-12">
                {tr(tCourse.gallery_title, lang)}
              </h2>
              <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 lg:gap-8">
                {gallery.slice(0, 9).map((it, i) => (
                  <div key={it.id} className="break-inside-avoid mb-6 lg:mb-8 zoom-frame" data-testid={`course-gallery-${i}`}>
                    <img src={it.image_url} alt={it.title || tr(page.eyebrow, lang)} loading="lazy" />
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* FAQ */}
        <section className="bg-[#FAFAFA] py-24 sm:py-32" data-testid="course-faq">
          <div className="max-w-[1100px] mx-auto px-6 sm:px-10">
            <p className="overline text-[#8B6D5E] mb-4">05</p>
            <h2 className="font-serif text-5xl sm:text-6xl tracking-tighter leading-[1] mb-12">
              {tr(tCourse.faq_title, lang)}
            </h2>
            <div className="border-t border-[#E8E1DA]">
              {faq.map((item, i) => {
                const isOpen = openFaq === i;
                return (
                  <div key={i} className="border-b border-[#E8E1DA]" data-testid={`course-faq-${i}`}>
                    <button
                      onClick={() => setOpenFaq(isOpen ? null : i)}
                      className="w-full flex items-center justify-between gap-8 py-6 text-left"
                      data-testid={`course-faq-toggle-${i}`}
                      aria-expanded={isOpen}
                    >
                      <span className="font-serif text-2xl sm:text-3xl tracking-tight pr-4">{item.q}</span>
                      <ChevronDown
                        size={22}
                        className={`shrink-0 text-[#8B6D5E] transition-transform duration-500 ${isOpen ? "rotate-180" : ""}`}
                      />
                    </button>
                    <div
                      className={`grid transition-all duration-500 ease-out ${isOpen ? "grid-rows-[1fr] opacity-100 pb-6" : "grid-rows-[0fr] opacity-0"}`}
                    >
                      <div className="overflow-hidden text-[#5C5C5C] leading-relaxed text-base max-w-3xl">{item.a}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA BLOCK */}
        <section className="bg-[#F4F0EC] py-24 sm:py-32" data-testid="course-cta">
          <div className="max-w-[1400px] mx-auto px-6 sm:px-10 grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <p className="overline text-[#8B6D5E] mb-4">06</p>
              <h2 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[1] mb-6">
                {tCourse.cta_block[lang]?.title || tCourse.cta_block.es.title}
              </h2>
              <p className="text-lg text-[#5C5C5C] max-w-xl mb-10">
                {tCourse.cta_block[lang]?.sub || tCourse.cta_block.es.sub}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 sm:items-center">
                <button onClick={() => setBookingOpen(true)} className="btn-primary" data-testid="course-cta-enroll">
                  {tr(tCourse.enroll_cta, lang)}
                </button>
                <a
                  href="https://www.instagram.com/abrilsuamkp/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="overline text-[#1A1A1A] flex items-center gap-2 link-underline"
                  data-testid="course-cta-instagram"
                >
                  <Instagram size={16} /> @abrilsuamkp
                </a>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="zoom-frame aspect-[4/5]">
                <img src={page.hero_image} alt={tr(page.eyebrow, lang)} loading="lazy" />
              </div>
            </div>
          </div>
        </section>

        {/* RELATED COURSES */}
        {otherCourses.length > 0 && (
          <section className="bg-[#FAFAFA] py-24 sm:py-32 border-t border-[#E8E1DA]" data-testid="related-courses">
            <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
              <p className="overline text-[#8B6D5E] mb-4">07</p>
              <h2 className="font-serif text-5xl sm:text-6xl tracking-tighter leading-[1] mb-12">
                {tr(tCourse.related, lang)}
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8 lg:gap-12">
                {otherCourses.map((o) => (
                  <Link
                    key={o.slug}
                    to={`/cursos/${o.slug}`}
                    className="group block"
                    data-testid={`related-course-${o.slug}`}
                  >
                    <div className="zoom-frame aspect-[16/10] mb-5">
                      <img src={o.hero_image} alt={tr(o.eyebrow, lang)} loading="lazy" />
                    </div>
                    <p className="overline text-[#8B6D5E] mb-2">{tr(o.eyebrow, lang)}</p>
                    <h3 className="font-serif text-3xl tracking-tight group-hover:text-[#8B6D5E] transition-colors max-w-md">
                      {tr(o.title, lang)}
                    </h3>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />

      <BookingDialog
        open={bookingOpen}
        onClose={() => setBookingOpen(false)}
        defaultService="course"
        courseId={course?.id}
      />
    </>
  );
}
